import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getQueueStatuses, QueueItemStatus } from '../api/tendQueueApi';

// Batches per-card queue-status lookups into one bulk request, exactly like
// PublishStatusContext: every QueueControl mounting in the same 50ms window
// shares one GET /api/tend/queue/items call, so a tended modal full of cards
// costs one request instead of one per card.
//
// Two rules keep the subscriber fan-out honest (Content Loading Performance,
// 2026-09-29 — the Tended/Published pages reload their feeds per announcement,
// and a tab return re-announcing every cached id produced 40+ identical list
// fetches in one second, gateway pool timeouts and 500s):
//  1. A re-read (refreshStatuses / refreshAll) announces an id only when its
//     status actually CHANGED against the cache. setKnownStatus — an action
//     this tab just took — always announces.
//  2. The cache is scoped to the brand (first path segment, kept by
//     <QueueStatusScope /> inside the Router): ids cached under one brand
//     never drive another brand's feeds.

interface QueueCacheEntry {
  status: QueueItemStatus;
  timestamp: number;
}

interface PendingWaiter {
  id: number;
  resolve: (status: QueueItemStatus) => void;
}

interface QueueStatusContextType {
  /** Batched + cached lookup — safe to call from every card on mount. */
  getQueueStatus: (tendContentId: number) => Promise<QueueItemStatus>;
  /** Overwrite the cache after an enqueue/dequeue/publish so siblings agree. */
  setKnownStatus: (tendContentId: number, status: QueueItemStatus) => void;
  /** Subscribe to queue mutations (any setKnownStatus call). The Tended
      page feeds listen here to refetch when a card's QueueControl acts.
      Returns an unsubscribe function. */
  subscribe: (cb: (tendContentId: number, status: QueueItemStatus) => void) => () => void;
  /** Force-refresh a set of ids (bypasses the cache) and notify
      subscribers of the ones that CHANGED — line mutations (reorder,
      move-to-front, dequeue) shift every sibling's rank, so the feed calls
      this after them. */
  refreshStatuses: (tendContentIds: number[]) => Promise<void>;
  /** Force-refresh every id cached under the current brand. Settings that
      shift projected fire times (auto publish, cadence fields) call this so
      pills show times immediately instead of waiting out the 60s cache
      TTL / 90s re-hydrate interval. Only changed ids are announced. */
  refreshAll: () => Promise<void>;
  /** Brand scope key (first path segment). A different key drops the
      cache; <QueueStatusScope /> keeps it in step with the route. */
  setScope: (key: string) => void;
}

const QueueStatusContext = createContext<QueueStatusContextType | null>(null);

const CACHE_TTL = 60000;
const BATCH_WINDOW_MS = 50;
const NOT_QUEUED: QueueItemStatus = { queued: false };

/** True when two statuses would render the same face / pill: a flat compare
    over every key of both (a missing key and an undefined one agree). No
    cached entry never equals anything — a first sighting is a change. */
export function sameQueueStatus(previous: QueueItemStatus | undefined, next: QueueItemStatus): boolean {
  if (!previous) return false;
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  for (const key of Array.from(keys)) {
    if ((previous as any)[key] !== (next as any)[key]) return false;
  }
  return true;
}

export const QueueStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<number, QueueCacheEntry>>(new Map());
  const batchRef = useRef<PendingWaiter[]>([]);
  const batchTimerRef = useRef<number | null>(null);
  const listenersRef = useRef<Set<(id: number, status: QueueItemStatus) => void>>(new Set());

  const flushBatch = useCallback(async () => {
    batchTimerRef.current = null;
    const waiters = batchRef.current;
    batchRef.current = [];
    if (waiters.length === 0) return;

    const ids = Array.from(new Set(waiters.map((w) => w.id)));
    try {
      const statuses = await getQueueStatuses(ids);
      const now = Date.now();
      ids.forEach((id) => {
        const status = statuses[String(id)] || NOT_QUEUED;
        cacheRef.current.set(id, { status, timestamp: now });
      });
      waiters.forEach((w) => w.resolve(statuses[String(w.id)] || NOT_QUEUED));
    } catch (err) {
      // A failed fetch is NOT evidence of "not queued" — resolving
      // NOT_QUEUED here made every card drop its queued face whenever a
      // transient 401/network blip hit the bulk call. Fall back to the
      // last-known cached state (even expired); only brand-new ids with
      // no history resolve as not-queued. State heals on the next poll.
      console.debug('Bulk queue-status fetch failed:', err);
      waiters.forEach((w) => {
        const stale = cacheRef.current.get(w.id);
        w.resolve(stale ? stale.status : NOT_QUEUED);
      });
    }
  }, []);

  const getQueueStatus = useCallback((tendContentId: number): Promise<QueueItemStatus> => {
    const cached = cacheRef.current.get(tendContentId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return Promise.resolve(cached.status);
    }
    return new Promise((resolve) => {
      batchRef.current.push({ id: tendContentId, resolve });
      if (batchTimerRef.current === null) {
        batchTimerRef.current = window.setTimeout(flushBatch, BATCH_WINDOW_MS);
      }
    });
  }, [flushBatch]);

  const setKnownStatus = useCallback((tendContentId: number, status: QueueItemStatus) => {
    cacheRef.current.set(tendContentId, { status, timestamp: Date.now() });
    listenersRef.current.forEach((cb) => { try { cb(tendContentId, status); } catch (e) { /* listener error */ } });
  }, []);

  const subscribe = useCallback((cb: (id: number, status: QueueItemStatus) => void) => {
    listenersRef.current.add(cb);
    return () => { listenersRef.current.delete(cb); };
  }, []);

  const refreshStatuses = useCallback(async (tendContentIds: number[]) => {
    if (tendContentIds.length === 0) return;
    try {
      const statuses = await getQueueStatuses(tendContentIds);
      const now = Date.now();
      tendContentIds.forEach((id) => {
        const status = statuses[String(id)] || NOT_QUEUED;
        const previous = cacheRef.current.get(id)?.status;
        cacheRef.current.set(id, { status, timestamp: now });
        // Announce a CHANGE, never a re-read that found the same state.
        if (sameQueueStatus(previous, status)) return;
        listenersRef.current.forEach((cb) => { try { cb(id, status); } catch (e) { /* listener error */ } });
      });
    } catch (err) {
      console.debug('Queue-status refresh failed:', err);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await refreshStatuses(Array.from(cacheRef.current.keys()));
  }, [refreshStatuses]);

  // Brand scope: a channel feed reloads on a same-platform announcement
  // about an id it doesn't list — with a session-wide cache, every queued
  // card of every brand visited earlier qualified, on every tab return.
  const scopeRef = useRef<string | null>(null);
  const setScope = useCallback((key: string) => {
    if (scopeRef.current !== null && scopeRef.current !== key) {
      cacheRef.current.clear();
    }
    scopeRef.current = key;
  }, []);

  // Tab return → re-read every ticket on screen: a relink elsewhere resumes
  // held tickets server-side, a dashboard unlink closes a line, and the
  // faces should say so without a reload. Throttled like the connections.
  const lastRefocusRef = useRef(0);
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefocusRef.current < 20000) return;
      lastRefocusRef.current = now;
      if (cacheRef.current.size) refreshAll();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [refreshAll]);

  return (
    <QueueStatusContext.Provider value={{ getQueueStatus, setKnownStatus, subscribe, refreshStatuses, refreshAll, setScope }}>
      {children}
    </QueueStatusContext.Provider>
  );
};

export const useQueueStatus = () => {
  const context = useContext(QueueStatusContext);
  if (!context) {
    throw new Error('useQueueStatus must be used within QueueStatusProvider');
  }
  return context;
};

/** Renders nothing; keeps the provider's brand scope in step with the route
    (the provider itself sits outside the Router). Mount once inside it. */
export const QueueStatusScope: React.FC = () => {
  const { pathname } = useLocation();
  const { setScope } = useQueueStatus();
  const key = pathname.split('/')[1] || '';
  useEffect(() => { setScope(key); }, [key, setScope]);
  return null;
};

export default QueueStatusContext;
